import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  CheckCircle2,
  LoaderCircle,
  RotateCcw,
  SkipForward,
  Smartphone,
  Waves,
} from "lucide-react";

const DURATION_SECONDS = 45;
const MAX_ATTEMPT_SECONDS = 90;

const median = (values) => {
  if (!values.length) return 0;
  const ordered = [...values].sort((a, b) => a - b);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2
    ? ordered[middle]
    : (ordered[middle - 1] + ordered[middle]) / 2;
};

const standardDeviation = (values) => {
  if (values.length < 2) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.sqrt(
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
      values.length,
  );
};

export function analyzePpgSamples(samples) {
  if (!samples || samples.length < 300) {
    return { valid: false, reason: "Not enough usable signal." };
  }
  const times = samples.map((sample) => sample.time);
  const values = samples.map((sample) => sample.value);
  const smoothed = values.map((_, index) => {
    const start = Math.max(0, index - 8);
    const window = values.slice(start, index + 1);
    return window.reduce((sum, value) => sum + value, 0) / window.length;
  });
  const detrended = smoothed.map((value, index) => {
    const start = Math.max(0, index - 30);
    const baseline =
      smoothed.slice(start, index + 1).reduce((sum, point) => sum + point, 0) /
      (index - start + 1);
    return value - baseline;
  });
  const mean = detrended.reduce((sum, value) => sum + value, 0) / detrended.length;
  const deviation = Math.sqrt(
    detrended.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
      detrended.length,
  );
  if (!Number.isFinite(deviation) || deviation < 0.15) {
    return { valid: false, reason: "The pulse signal was too weak." };
  }

  const threshold = mean + deviation * 0.35;
  const peaks = [];
  for (let index = 2; index < detrended.length - 2; index += 1) {
    const isPeak =
      detrended[index] > threshold &&
      detrended[index] >= detrended[index - 1] &&
      detrended[index] > detrended[index + 1];
    if (!isPeak) continue;
    const time = times[index];
    if (!peaks.length || time - peaks[peaks.length - 1] >= 320) peaks.push(time);
  }

  const intervals = peaks
    .slice(1)
    .map((time, index) => time - peaks[index])
    .filter((interval) => interval >= 350 && interval <= 1600);
  if (intervals.length < 18) {
    return { valid: false, reason: "A stable pulse could not be identified." };
  }

  const typicalInterval = median(intervals);
  const heartRate = Math.round(60000 / typicalInterval);
  const squaredDifferences = intervals
    .slice(1)
    .map((interval, index) => (interval - intervals[index]) ** 2);
  const hrvMs = Math.round(
    Math.sqrt(
      squaredDifferences.reduce((sum, value) => sum + value, 0) /
        squaredDifferences.length,
    ),
  );
  const intervalMean =
    intervals.reduce((sum, value) => sum + value, 0) / intervals.length;
  const intervalSd = Math.sqrt(
    intervals.reduce((sum, value) => sum + (value - intervalMean) ** 2, 0) /
      intervals.length,
  );
  const variability = intervalSd / intervalMean;
  const quality =
    intervals.length >= 35 && variability < 0.18
      ? "good"
      : intervals.length >= 24 && variability < 0.25
        ? "fair"
        : "low";

  if (heartRate < 35 || heartRate > 200 || hrvMs > 300 || quality === "low") {
    return {
      valid: false,
      reason: "Signal quality was too low for a usable reading.",
    };
  }

  return {
    valid: true,
    source: "camera",
    heartRate,
    hrvMs,
    quality,
    durationSeconds: Math.round((times[times.length - 1] - times[0]) / 1000),
  };
}

const FingerPlacementGuide = () => (
  <div className="mt-4 grid gap-4 rounded-2xl p-4 forr-surface-tint sm:grid-cols-[150px_1fr] sm:items-center">
    <div className="relative mx-auto h-[112px] w-[86px] rounded-[20px] border-2 border-black/15 bg-white shadow-sm">
      <div className="absolute left-3 top-3 h-6 w-6 rounded-full border-4 border-slate-700 bg-slate-900" />
      <div className="absolute right-3 top-3 h-5 w-5 rounded-full border border-amber-300 bg-amber-100 shadow-[0_0_14px_rgba(251,191,36,0.75)]" />
      <div
        className="absolute -left-4 -top-2 h-[78px] w-[58px] rotate-[18deg] rounded-[30px] border border-rose-200 bg-rose-100/95 shadow-md"
        aria-hidden="true"
      />
      <div className="absolute bottom-2 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-black/10" />
    </div>
    <div>
      <div className="font-medium forr-text-ink">Cover both the rear camera and flash</div>
      <p className="mt-2 text-xs leading-relaxed forr-text-mute">
        Rest the pad of one fingertip over both circles. Keep your hand still. If the app detects your finger but not a pulse, slightly reduce pressure or shift the fingertip until it says it is reading.
      </p>
    </div>
  </div>
);

const Waveform = ({ values }) => {
  const points = useMemo(() => {
    if (!values.length) return "0,25 100,25";
    const minimum = Math.min(...values);
    const maximum = Math.max(...values);
    const range = Math.max(0.5, maximum - minimum);
    return values
      .map((value, index) => {
        const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100;
        const y = 44 - ((value - minimum) / range) * 36;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }, [values]);

  return (
    <div className="mt-4 overflow-hidden rounded-2xl bg-white p-3" style={{ border: "1px solid var(--forr-line-soft)" }}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] forr-text-mute">
          <Waves size={14} /> Live signal
        </div>
        <div className="text-[10px] forr-text-mute">Movement should form a repeating wave</div>
      </div>
      <svg viewBox="0 0 100 50" className="mt-2 h-16 w-full" preserveAspectRatio="none" role="img" aria-label="Live pulse signal">
        <line x1="0" x2="100" y1="25" y2="25" stroke="rgba(15,117,110,0.12)" strokeWidth="1" />
        <polyline
          points={points}
          fill="none"
          stroke="var(--forr-teal)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};

const LIVE_COPY = {
  waiting: {
    title: "Not able to read yet",
    body: "Cover both the rear camera and flash. Keep the fingertip still.",
  },
  finger: {
    title: "Finger detected",
    body: "Adjust pressure or move slightly until a repeating pulse appears.",
  },
  pulse: {
    title: "Pulse detected",
    body: "Hold still. The app is checking whether the signal remains stable.",
  },
  stable: {
    title: "It is reading",
    body: "Keep still and breathe normally while heart rate and HRV are estimated.",
  },
};

const CameraHrv = ({ onReading }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const samplesRef = useRef([]);
  const recentSignalRef = useRef([]);
  const startedAtRef = useRef(0);
  const lastSampleAtRef = useRef(0);
  const lastValidAtRef = useRef(0);
  const usableMsRef = useRef(0);
  const lastWaveformAtRef = useRef(0);
  const [status, setStatus] = useState("idle");
  const [sensorState, setSensorState] = useState("waiting");
  const [signalQuality, setSignalQuality] = useState("none");
  const [progress, setProgress] = useState(0);
  const [waveform, setWaveform] = useState([]);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [manualHeartRate, setManualHeartRate] = useState("");
  const [manualError, setManualError] = useState("");

  const stopCamera = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => stopCamera, []);

  const finishCameraReading = () => {
    stopCamera();
    const reading = analyzePpgSamples(samplesRef.current);
    if (reading.valid) {
      setResult(reading);
      setStatus("complete");
      setMessage("");
      onReading?.(reading);
    } else {
      setStatus("error");
      setMessage(
        `${reading.reason} Adjust your fingertip and try again, enter heart rate manually, or continue without this optional context.`,
      );
    }
  };

  const sampleFrame = (timestamp) => {
    const totalElapsed = timestamp - startedAtRef.current;

    if (
      videoRef.current?.readyState >= 2 &&
      timestamp - lastSampleAtRef.current >= 30
    ) {
      const previousSample = lastSampleAtRef.current;
      lastSampleAtRef.current = timestamp;
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d", { willReadFrequently: true });
      if (canvas && context) {
        canvas.width = 32;
        canvas.height = 32;
        context.drawImage(videoRef.current, 0, 0, 32, 32);
        const pixels = context.getImageData(8, 8, 16, 16).data;
        let red = 0;
        let green = 0;
        let blue = 0;
        for (let index = 0; index < pixels.length; index += 4) {
          red += pixels[index];
          green += pixels[index + 1];
          blue += pixels[index + 2];
        }
        const pixelCount = pixels.length / 4;
        const redMean = red / pixelCount;
        const greenMean = green / pixelCount;
        const blueMean = blue / pixelCount;
        const fingerDetected =
          redMean > 70 &&
          redMean > greenMean * 1.03 &&
          redMean > blueMean * 1.08;

        if (fingerDetected) {
          samplesRef.current.push({ time: timestamp, value: redMean });
          recentSignalRef.current = [...recentSignalRef.current, redMean].slice(-90);
          const deviation = standardDeviation(recentSignalRef.current.slice(-50));
          const validDelta =
            previousSample && timestamp - previousSample < 200
              ? timestamp - previousSample
              : 0;
          usableMsRef.current += validDelta;
          lastValidAtRef.current = timestamp;

          const usableSeconds = usableMsRef.current / 1000;
          if (usableSeconds >= 5 && deviation >= 0.55) {
            setSensorState("stable");
            setSignalQuality(deviation >= 1.1 ? "good" : "fair");
          } else if (usableSeconds >= 2 && deviation >= 0.35) {
            setSensorState("pulse");
            setSignalQuality("fair");
          } else {
            setSensorState("finger");
            setSignalQuality("low");
          }

          if (timestamp - lastWaveformAtRef.current >= 100) {
            lastWaveformAtRef.current = timestamp;
            setWaveform([...recentSignalRef.current.slice(-60)]);
          }
        } else {
          if (lastValidAtRef.current && timestamp - lastValidAtRef.current > 1000) {
            recentSignalRef.current = [];
            setWaveform([]);
          }
          setSensorState("waiting");
          setSignalQuality("none");
        }
      }
    }

    const nextProgress = Math.min(
      100,
      (usableMsRef.current / (DURATION_SECONDS * 1000)) * 100,
    );
    setProgress(nextProgress);

    if (usableMsRef.current >= DURATION_SECONDS * 1000) {
      finishCameraReading();
      return;
    }
    if (totalElapsed >= MAX_ATTEMPT_SECONDS * 1000) {
      stopCamera();
      setStatus("error");
      setMessage(
        "A stable signal was not available long enough. Adjust your finger, enter heart rate manually, or skip this optional measurement.",
      );
      return;
    }
    frameRef.current = requestAnimationFrame(sampleFrame);
  };

  const start = async () => {
    stopCamera();
    setMessage("");
    setResult(null);
    setProgress(0);
    setWaveform([]);
    setSensorState("waiting");
    setSignalQuality("none");
    samplesRef.current = [];
    recentSignalRef.current = [];
    usableMsRef.current = 0;
    lastValidAtRef.current = 0;
    try {
      setStatus("requesting");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities?.();
      if (capabilities?.torch) {
        try {
          await track.applyConstraints({ advanced: [{ torch: true }] });
        } catch (_error) {
          // Some browsers expose torch capability but do not allow direct control.
        }
      }
      setStatus("measuring");
      startedAtRef.current = performance.now();
      lastSampleAtRef.current = 0;
      lastWaveformAtRef.current = 0;
      frameRef.current = requestAnimationFrame(sampleFrame);
    } catch (_error) {
      stopCamera();
      setStatus("error");
      setMessage(
        "Camera access was unavailable. Allow camera access, enter heart rate manually, or continue without this optional reading.",
      );
    }
  };

  const useManualHeartRate = () => {
    const heartRate = Number(manualHeartRate);
    if (!Number.isInteger(heartRate) || heartRate < 35 || heartRate > 220) {
      setManualError("Enter a current heart rate from 35 to 220 bpm.");
      return;
    }
    stopCamera();
    const reading = {
      valid: true,
      source: "manual",
      heartRate,
      hrvMs: null,
      quality: "manual",
      durationSeconds: 0,
    };
    setManualError("");
    setResult(reading);
    setStatus("complete");
    setMessage("");
    onReading?.(reading);
  };

  const skip = () => {
    stopCamera();
    setStatus("skipped");
    setResult(null);
    setMessage("");
    setProgress(0);
    onReading?.(null);
  };

  const live = LIVE_COPY[sensorState];
  const qualityBars = signalQuality === "good" ? 4 : signalQuality === "fair" ? 3 : signalQuality === "low" ? 1 : 0;
  const cameraSupported =
    typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);

  return (
    <div className="rounded-[22px] p-5 forr-panel-information">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="forr-overline">Optional physiological context</div>
          <h3 className="mt-2 font-display text-xl forr-text-ink">
            Current heart rate and camera HRV
          </h3>
        </div>
        <Camera size={22} className="shrink-0 forr-text-teal-deep" />
      </div>
      <p className="mt-3 text-sm leading-relaxed forr-text-soft">
        Heart rate and HRV can add context, but the directly reported state and physical readiness remain the main decision inputs.
      </p>

      <FingerPlacementGuide />
      <video ref={videoRef} muted playsInline className="hidden" />
      <canvas ref={canvasRef} className="hidden" />

      {status === "measuring" || status === "requesting" ? (
        <div className="mt-5">
          <div className="rounded-2xl bg-white p-4" style={{ border: "1px solid var(--forr-line-soft)" }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <LoaderCircle size={19} className="mt-0.5 animate-spin forr-text-teal-deep" />
                <div>
                  <div className="font-medium forr-text-ink">
                    {status === "requesting" ? "Requesting camera access" : live.title}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed forr-text-mute">
                    {status === "requesting"
                      ? "Allow access to the rear camera."
                      : live.body}
                  </p>
                </div>
              </div>
              <div>
                <div className="flex gap-1" aria-label={`Signal quality ${signalQuality}`}>
                  {[1, 2, 3, 4].map((bar) => (
                    <span
                      key={bar}
                      className="w-1.5 rounded-full"
                      style={{
                        height: `${8 + bar * 4}px`,
                        backgroundColor:
                          bar <= qualityBars ? "var(--forr-teal)" : "var(--forr-line-soft)",
                      }}
                    />
                  ))}
                </div>
                <div className="mt-1 text-[8px] uppercase tracking-[0.12em] forr-text-mute">
                  {signalQuality === "none" ? "No signal" : signalQuality}
                </div>
              </div>
            </div>
            <Waveform values={waveform} />
            <div className="mt-4 h-2 overflow-hidden rounded-full" style={{ backgroundColor: "var(--forr-line-soft)" }}>
              <div
                className="h-full rounded-full transition-[width] duration-200"
                style={{ width: `${progress}%`, backgroundColor: "var(--forr-teal)" }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[10px] forr-text-mute">
              <span>{Math.round(progress)}% usable signal</span>
              <span>About {DURATION_SECONDS} seconds of reading</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              stopCamera();
              setStatus("idle");
              setProgress(0);
              setSensorState("waiting");
            }}
            className="mt-4 text-sm forr-text-mute underline"
          >
            Cancel camera reading
          </button>
        </div>
      ) : result ? (
        <div className="mt-5 rounded-2xl bg-teal-tint p-4">
          <div className="flex items-center gap-2 text-sm font-medium forr-text-ink">
            <CheckCircle2 size={18} className="forr-text-teal-deep" />
            {result.source === "manual"
              ? "Manual heart rate saved"
              : `Reading captured · ${result.quality} signal`}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="font-display text-2xl">{result.heartRate}</div>
              <div className="text-xs forr-text-mute">current bpm</div>
            </div>
            <div>
              <div className="font-display text-2xl">
                {result.hrvMs ? `${result.hrvMs} ms` : "—"}
              </div>
              <div className="text-xs forr-text-mute">
                {result.hrvMs ? "estimated RMSSD" : "HRV unavailable"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={start}
            className="mt-4 inline-flex items-center gap-2 text-sm forr-text-teal-deep"
          >
            <RotateCcw size={15} /> Try camera again
          </button>
        </div>
      ) : (
        <div className="mt-5">
          {message && (
            <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
              {message}
            </p>
          )}
          {status === "skipped" && (
            <p className="mb-4 rounded-xl p-3 text-sm forr-surface-tint forr-text-soft">
              Optional heart-rate and HRV context skipped. The current-state check-in remains fully available.
            </p>
          )}
          <button
            type="button"
            onClick={start}
            disabled={!cameraSupported}
            className="rounded-full px-5 py-3 text-sm font-medium forr-btn-secondary disabled:opacity-50"
          >
            {status === "error" ? "Try camera again" : "Measure with camera"}
          </button>
        </div>
      )}

      <div className="mt-5 border-t pt-5" style={{ borderColor: "var(--forr-line-soft)" }}>
        <div className="flex items-center gap-2">
          <Smartphone size={17} className="forr-text-teal-deep" />
          <div className="font-medium forr-text-ink">Input current heart rate</div>
        </div>
        <p className="mt-2 text-xs leading-relaxed forr-text-mute">
          Use a recent reading from a watch, chest strap, pulse oximeter, gym machine, or manual count. HRV will remain unavailable.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            type="number"
            inputMode="numeric"
            min="35"
            max="220"
            value={manualHeartRate}
            onChange={(event) => setManualHeartRate(event.target.value)}
            placeholder="BPM"
            className="min-w-0 flex-1 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm forr-text-ink"
            aria-label="Current heart rate in beats per minute"
          />
          <button
            type="button"
            onClick={useManualHeartRate}
            className="rounded-xl px-4 py-3 text-sm font-medium forr-btn-secondary"
          >
            Use BPM
          </button>
        </div>
        {manualError && <p className="mt-2 text-xs text-red-700">{manualError}</p>}
      </div>

      <button
        type="button"
        onClick={skip}
        className="mt-4 inline-flex items-center gap-2 text-xs forr-text-mute"
      >
        <SkipForward size={14} /> Skip heart rate and HRV
      </button>
      <p className="mt-3 text-[10px] leading-relaxed forr-text-mute">
        Camera PPG is an optional estimate, not an ECG, diagnosis, or emergency monitor. Compare trends under similar resting conditions rather than interpreting one reading alone.
      </p>
    </div>
  );
};

export default CameraHrv;
